import { friendlyProblem, logClientError, networkProblem } from "@stagvista/ui";

import { CLOUD_API_BASE_URL } from "./cloud-api.js";

/** RFC 9457 shape `apps/cloud-api`'s `ProblemDetailsExceptionFilter` always sends — defined locally rather than imported from `apps/desktop`'s identical type, since this app has no build dependency on any sibling app. */
export interface ProblemDetails {
  readonly type: string;
  readonly title: string;
  readonly status: number;
  readonly detail?: string;
  readonly instance?: string;
  readonly [key: string]: unknown;
}

export class CloudApiError extends Error {
  constructor(readonly problem: ProblemDetails) {
    super(problem.detail ?? problem.title);
    this.name = "CloudApiError";
  }
}

export interface CloudApiClientOptions {
  readonly baseUrl?: string;
  readonly getToken: () => string | null;
  /**
   * Called at most once per request when the server comes back 401.
   * Returning `true` means a fresh token is now available via `getToken()`
   * and this one request should be retried; `false` means the session is
   * truly gone — the caller (`CloudSessionProvider`) has already signed out
   * and is redirecting to `/sign-in`, so the original 401 is left to
   * propagate as-is (the page is about to unload anyway). Omit for a
   * client with no session-recovery story.
   */
  readonly onUnauthorized?: () => Promise<boolean>;
}

/** Thin `fetch` wrapper for calls into `apps/cloud-api` — structurally the same shape as `apps/desktop`'s own `CloudApiClient`, copied rather than shared since the two apps have no other reason to depend on each other. */
export class CloudApiClient {
  private readonly baseUrl: string;

  constructor(private readonly options: CloudApiClientOptions) {
    this.baseUrl = options.baseUrl ?? CLOUD_API_BASE_URL;
  }

  async request<T>(
    method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE",
    path: string,
    body?: unknown,
  ): Promise<T> {
    return this.send<T>(method, path, body, true);
  }

  private async send<T>(
    method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE",
    path: string,
    body: unknown,
    allowRetry: boolean,
  ): Promise<T> {
    const token = this.options.getToken();
    const headers: Record<string, string> = {};
    if (token) headers.authorization = `Bearer ${token}`;
    if (body !== undefined) headers["content-type"] = "application/json";

    const response = await this.fetchFriendly(method, `${this.baseUrl}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (response.status === 401 && allowRetry && this.options.onUnauthorized) {
      if (await this.options.onUnauthorized()) return this.send<T>(method, path, body, false);
    }

    if (!response.ok) {
      const problem = (await response.json().catch(() => null)) as ProblemDetails | null;
      throw this.toError(
        problem ?? {
          type: "about:blank",
          title: response.statusText || "Request failed",
          status: response.status,
        },
        method,
        path,
      );
    }

    if (response.status === 204) return undefined as T;
    const text = await response.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  /** For binary/file responses (the schedule export routes) — same auth/error/retry handling as `request`, but returns the raw body as a `Blob` instead of JSON-parsing it. */
  async requestBlob(method: "GET" | "POST", path: string): Promise<Blob> {
    return this.sendBlob(method, path, true);
  }

  private async sendBlob(method: "GET" | "POST", path: string, allowRetry: boolean): Promise<Blob> {
    const token = this.options.getToken();
    const headers: Record<string, string> = {};
    if (token) headers.authorization = `Bearer ${token}`;

    const response = await this.fetchFriendly(method, `${this.baseUrl}${path}`, {
      method,
      headers,
    });

    if (response.status === 401 && allowRetry && this.options.onUnauthorized) {
      if (await this.options.onUnauthorized()) return this.sendBlob(method, path, false);
    }

    if (!response.ok) {
      const problem = (await response.json().catch(() => null)) as ProblemDetails | null;
      throw this.toError(
        problem ?? {
          type: "about:blank",
          title: response.statusText || "Request failed",
          status: response.status,
        },
        method,
        path,
      );
    }

    return response.blob();
  }

  /** `fetch`, but a request that never got an answer throws a readable error (the raw browser text is kept for the log, not the screen). */
  private async fetchFriendly(method: string, url: string, init: RequestInit): Promise<Response> {
    try {
      return await fetch(url, init);
    } catch (cause) {
      const problem = networkProblem(cause, { target: "cloud", method, url });
      logClientError(problem, { target: "cloud", method, url });
      throw new CloudApiError(problem);
    }
  }

  /** A failed response as a CloudApiError whose text is readable and carries a reference code. */
  private toError(problem: ProblemDetails, method: string, path: string): CloudApiError {
    const friendly = friendlyProblem(problem);
    logClientError(friendly, { target: "cloud", method, url: `${this.baseUrl}${path}` });
    return new CloudApiError(friendly);
  }
}
